import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ASSETS } from '../config/assets';
import BrandLogo, { TAPELLIGENCE_URL } from './BrandLogo';

const Footer = () => {
  const { language, t } = useLanguage();

  const rcPath = language === 'fr' ? '/fr/culture-des-avis' : '/review-culture';
  const rcLabel = language === 'fr' ? 'Culture des avis' : 'Review Culture';

  const capabilities = [
    { path: '/platform', label: t('nav.platform') },
    { path: '/positive-reviews', label: t('nav.positiveReviews') },
    { path: '/b-heard', label: t('nav.reviewShield') },
    { path: '/global-review', label: t('nav.globalReview') },
    { path: '/rr360', label: t('nav.rrr360') },
  ];

  const company = [
    { path: '/guest-communication', label: t('nav.guestCommunication') },
    { path: '/insights', label: t('nav.insights') },
    { path: '/hardware', label: t('nav.hardware') },
    { path: '/implementation', label: t('nav.implementation') },
    { path: '/supporting-data', label: t('nav.supportingData') },
    { path: rcPath, label: rcLabel },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main footer grid: Brand | Capabilities | Company | Connect + Tapelligence */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <BrandLogo className="h-24" />
            </div>
            <p className="text-white/50 text-sm leading-relaxed">
              {t('footer.tagline')}
            </p>
            <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-xs text-white/70 font-medium uppercase tracking-wider">Zero POS Integration</p>
              <p className="text-xs text-white/40 mt-1">Independent. Lightweight. Simple.</p>
            </div>
          </div>

          {/* Capabilities */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-4 text-white">
              {t('footer.capabilities')}
            </h4>
            <ul className="space-y-2">
              {capabilities.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/50 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-4 text-white">
              {t('footer.company')}
            </h4>
            <ul className="space-y-2">
              {company.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/50 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect + Tapelligence logo on far right */}
          <div className="flex flex-col">
            <h4 className="font-heading text-lg font-medium mb-4 text-white">
              {t('footer.connect')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/contact" className="text-white/50 hover:text-white text-sm transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <a href="https://portal.yulontap.com" className="text-white/50 hover:text-white text-sm transition-colors" target="_blank" rel="noopener noreferrer">
                  {t('nav.portal')}
                </a>
              </li>
            </ul>

            {/* Tapelligence parent brand — far right, bottom of this column */}
            <div className="mt-auto pt-8">
              <a
                href={TAPELLIGENCE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tapelligence-logo"
                className="inline-block opacity-80 hover:opacity-100 transition-opacity duration-300"
              >
                <img
                  src={ASSETS.TAPELLIGENCE_LOGO}
                  alt="Tapelligence"
                  className="w-[220px] sm:w-[240px] lg:w-[260px] h-auto"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar — copyright + privacy */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              {t('footer.copyright')}
            </p>
            <div className="flex gap-6">
              <Link to="#" className="text-white/40 hover:text-white text-sm transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="#" className="text-white/40 hover:text-white text-sm transition-colors">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
